/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { Diagnostics, Operations, Payload } from '@tivocorp/tivo-webapp-loggers';
import { SamlToken } from 'client-code/device-binding-service';
import { Error403CodeEnum } from 'client-code/iptv-login';

import { ACCESS_TOKEN_BUFFER_TIME } from '../../config';
import {
    EmptyResponseError,
    PartialResponseError,
    UnauthenticatedError,
    UnauthorisedError,
} from '../error';
import { IptvLoginError } from '../error/IptvLoginError';
import { AuthServicesClientImpl } from '../impls/AuthServicesClientImpl';
import { SlsServiceGlobalEndpointsService } from '../infrastructure/SlsServiceGlobalEndpointsService';
import { EventProperties, Events, SignInStates } from '../logging';
import { Log } from '../logging/Log';
import { ScopedDomainTokenModel } from '../videoplayer/impls/ScopedDomianTokenModel';
import { SDTInfo } from '../videoplayer/interfaces';
import { DeviceModel } from './DeviceModel';

export class TokenRefreshMechansim {
    private samlTokenTimerId!: NodeJS.Timeout;

    private accessTokenTimerId!: NodeJS.Timeout;

    private oAuthServiceClientImpl: AuthServicesClientImpl;

    private static refreshAttemptsOnAccountLocked = 0;

    static oTokenRefreshMechanism;

    private mSDTModel;

    private static tokenRefreshTimeOut;

    private TAG = 'TokenRefreshMechansim';

    private constructor() {
        this.mSDTModel = ScopedDomainTokenModel.getInstance();
        this.oAuthServiceClientImpl = new AuthServicesClientImpl({
            iptvUrl: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                SlsServiceGlobalEndpointsService.IPTV_LOGIN_SERVICE,
            ),
            deviceBinding: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                SlsServiceGlobalEndpointsService.DEVICE_BINDING_SERVICE,
            ),
        });
    }

    private async refreshSamlToken() {
        let samlToken: SamlToken;
        try {
            samlToken = await this.oAuthServiceClientImpl.getSamlToken();
            Diagnostics.info(this.TAG, 'refreshSamlToken success', samlToken);
        } catch (error) {
            Log.e(this.TAG, 'refreshSamlToken', error);
            Diagnostics.error(this.TAG, 'error during refreshSamlToken');
            Diagnostics.error(this.TAG, 'stacktrace:', error);
            samlToken = await this.oAuthServiceClientImpl.waitForSamlToken();
            Diagnostics.info(this.TAG, 'refreshSamlToken success after recovery', samlToken);
        } finally {
            DeviceModel.setSamlTokenWithExpiry(
                samlToken!.samlToken,
                new Date(samlToken!.samlTokenExpirationTime),
            );
        }
    }

    private async refreshAccessToken() {
        Log.i(this.TAG, 'in refresh access token');
        let samlToken;
        try {
            samlToken = DeviceModel.getSamlTokenWithExpiry().samlToken;
        } catch (error) {
            Log.e(
                this.TAG,
                'refreshAccessToken:: active saml token not available, refetching',
                error,
            );
            Diagnostics.error(
                this.TAG,
                'refreshAccessToken:: active saml token not available, refetching',
            );
            Diagnostics.error(this.TAG, 'stacktrace:', error);
            clearTimeout(this.samlTokenTimerId);
            Diagnostics.info(this.TAG, 'waiting for saml token refresh');
            await this.refreshSamlToken();
            Diagnostics.info(this.TAG, 'saml token refresh successful');
        }

        samlToken = DeviceModel.getSamlToken();

        try {
            const iptvLoginResponse =
                await this.oAuthServiceClientImpl.getIptvLoginBySamlToken(samlToken);
            Log.i(this.TAG, 'access token fetching success');
            Diagnostics.info(this.TAG, 'access token fetching success');
            DeviceModel.setAccessTokenWithExpiry(
                iptvLoginResponse.accessToken,
                new Date(iptvLoginResponse.tokenExpirationTime),
            );
            TokenRefreshMechansim.refreshAttemptsOnAccountLocked = 0;
        } catch (error: any) {
            let iptvError = error;
            if (!(error instanceof EmptyResponseError || error instanceof PartialResponseError)) {
                const iptvLoginError: any = error?.response?.data;
                iptvError = new IptvLoginError(
                    iptvLoginError?.message,
                    iptvLoginError?.code,
                    'access token error in token refresh mechanism',
                );
                const payload = new Payload();
                payload.add(EventProperties.SIGN_IN_STATE, SignInStates.ACCESS_TOKEN_GET);
                payload.add(EventProperties.EVENT_NAME, Events.ACCESS_TOKEN_FAILED);
                payload.add(EventProperties.ERROR_CODE, iptvError.getCode());
                payload.add(EventProperties.ERROR_TYPE, iptvError.getType());
                payload.add(EventProperties.ERROR_MESSAGE, iptvError.getMessage());
                payload.add(EventProperties.REFERENCE_TIME, new Date().getTime());
                Operations.log(payload);
                if (iptvLoginError.code === Error403CodeEnum.AuthsNotAuthorized) {
                    Log.e(this.TAG, 'device not authorized for current session');
                    TokenRefreshMechansim.refreshAttemptsOnAccountLocked += 1;
                }
            }
            Log.e(this.TAG, 'cannot fetch accessToken in token refresh mechanism', error);
            Diagnostics.error(this.TAG, 'cannot fetch accessToken in token refresh mechanism');
            Diagnostics.error(this.TAG, 'stacktrace:', error);
        } finally {
            if (TokenRefreshMechansim.refreshAttemptsOnAccountLocked <= 3) {
                this.scheduleAccessTokenRefresh();
            }
        }
    }

    static getInstance(): TokenRefreshMechansim {
        if (!TokenRefreshMechansim.oTokenRefreshMechanism)
            TokenRefreshMechansim.oTokenRefreshMechanism = new TokenRefreshMechansim();
        return TokenRefreshMechansim.oTokenRefreshMechanism;
    }

    scheduleAccessTokenRefresh = () => {
        clearTimeout(this.accessTokenTimerId);
        let timeToRefresh = 0;
        let shouldRefreshToken = true;
        try {
            const expiryTime = DeviceModel.getAccessTokenWithExpiry().tokenExpirationTime;
            timeToRefresh = expiryTime.getTime() - Date.now() - ACCESS_TOKEN_BUFFER_TIME;
            if (timeToRefresh < 0) timeToRefresh = 0;
            Diagnostics.info(
                this.TAG,
                'scheduleAccessTokenRefresh::time to refresh access token in ms',
                timeToRefresh,
            );
        } catch (error) {
            if (error instanceof UnauthorisedError) {
                Diagnostics.error(this.TAG, 'scheduleAccessTokenRefresh:: UnauthorisedError error');
                Diagnostics.error(this.TAG, 'stacktrace:', error);
                timeToRefresh = 0;
                Diagnostics.info(
                    this.TAG,
                    'scheduleAccessTokenRefresh::time to refresh access token when UnauthorisedError in ms',
                    timeToRefresh,
                );
            }
            if (error instanceof UnauthenticatedError) {
                Log.e(
                    this.TAG,
                    'User is unauthenticated for current session, not refreshing access token.',
                );
                Diagnostics.error(
                    this.TAG,
                    'User is unauthenticated for current session, not refreshing access token.',
                );
                Diagnostics.error(this.TAG, 'stacktrace:', error);
                shouldRefreshToken = false;
                Diagnostics.info(
                    this.TAG,
                    `scheduleAccessTokenRefresh:: shouldRefreshToken -> ${shouldRefreshToken}`,
                );
            }
        }
        if (shouldRefreshToken) {
            this.accessTokenTimerId = setTimeout(() => {
                this.refreshAccessToken();
            }, timeToRefresh);
        }
    };

    async onDemandAccessTokenRefresh() {
        clearTimeout(this.accessTokenTimerId);
        await this.refreshAccessToken();
        this.scheduleAccessTokenRefresh();
    }

    public scheduleDomainTokenRefresh(sdtInfo: SDTInfo): void {
        clearTimeout(TokenRefreshMechansim.tokenRefreshTimeOut);
        Diagnostics.info(
            this.TAG,
            `next earliest expiring scope domain token is: ${sdtInfo.drmCustomDomain} in ${sdtInfo.refreshTime} seconds`,
        );

        TokenRefreshMechansim.tokenRefreshTimeOut = setTimeout(async () => {
            try {
                await this.mSDTModel?.getToken(
                    sdtInfo.drmCustomDomain,
                    sdtInfo.drmCustomHeaderAuthScheme,
                    false,
                    true,
                );
            } catch (error) {
                // Handle token refresh error silently or log to appropriate logger
            }
        }, sdtInfo.refreshTime);
    }
}
